import { Dialog } from '@headlessui/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';

export default function Lightbox({ car, onClose }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const fallbackImage = 'https://picsum.photos/1200/400?random=2';
  const images = car.images && car.images.length > 0 
    ? car.images.map(img => img.startsWith('http') ? img : `${process.env.REACT_APP_API_URL}${img}`)
    : [fallbackImage];

  const prevImage = () => {
    setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleImageError = (e) => {
    e.target.src = fallbackImage;
    e.target.alt = 'Image not available';
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <Dialog open={true} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      
      {/* Container modified to support vertical scrolling on mobile and locked height on desktop */}
      <Dialog.Panel className="bg-white rounded-2xl max-w-5xl w-full flex flex-col md:flex-row shadow-2xl relative overflow-y-auto md:overflow-hidden max-h-[92vh] md:max-h-[85vh] border border-gray-100 transition-all duration-300">
        
        {/* Close Button */}
        <button  
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-600 bg-white/80 hover:bg-gray-100 p-1.5 rounded-full z-20 shadow-sm border border-gray-100 transition-colors"
          aria-label="Close Lightbox"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Left Side: Dynamic Height Image Showcase & Thumbnails Gallery */}
        <div className="w-full md:w-2/3 p-5 flex flex-col justify-between border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0">
          
          {/* Responsive Height Container (Generous h-60 on mobile) */}
          <div className="relative w-full h-60 sm:h-80 md:h-96 lg:h-[45vh] bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">
            <img
              src={images[currentImageIndex]}
              alt={`${car.make} ${car.model} image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover md:object-contain" // Cover matches responsive standard on mobile
              onError={handleImageError}
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          
          {/* Thumbnails grid */}
          <div className="grid grid-cols-4 gap-2 mt-4 overflow-y-auto max-h-24 py-1 flex-shrink-0">
            {images.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`${car.make} ${car.model} thumbnail ${index + 1}`}
                className={`w-full h-16 object-cover rounded-lg cursor-pointer border transition-all ${
                  index === currentImageIndex 
                    ? 'border-2 border-blue-900 shadow' 
                    : 'border-gray-200 opacity-60 hover:opacity-100'
                }`}
                onClick={() => handleThumbnailClick(index)}
                onError={handleImageError}
              />
            ))}
          </div>
        </div>

        {/* Right Side: Key Details, Wrap-safe specs table, and CTA Action Button */}
        {/* On desktop (md), this scroll-container operates independently */}
        <div className="w-full md:w-1/3 p-5 sm:p-6 flex flex-col justify-between overflow-y-auto bg-gray-50/50 md:max-h-[85vh]">
          <div className="space-y-6">
            <div>
              <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-2.5 py-1 rounded-full uppercase tracking-wider select-none">
                {car.condition || 'Foreign Used'}
              </span>
              <h2 className="text-xl font-extrabold text-blue-950 font-sans mt-2 tracking-tight">
                {car.make} {car.model}
              </h2>
              <span className="text-xs font-semibold text-gray-400">Year model: {car.year}</span>
            </div>

            {/* Technical Parameters Table with Wrap-Safe text alignments */}
            <div className="bg-white p-4 rounded-xl border border-gray-150 shadow-sm space-y-2 text-[11px] sm:text-xs text-gray-700">
              <div className="flex justify-between items-center gap-3 py-1 border-b border-gray-50">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Body Type</span>
                <span className="font-bold text-blue-950 text-right">{car.body_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center gap-3 py-1 border-b border-gray-50">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Transmission</span>
                <span className="font-bold text-blue-950 text-right">{car.transmission || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center gap-3 py-1 border-b border-gray-50">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Mileage</span>
                <span className="font-bold text-blue-950 text-right">{car.mileage?.toLocaleString() || 'N/A'} Miles</span>
              </div>
              <div className="flex justify-between items-center gap-3 py-1 border-b border-gray-50">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Engine size</span>
                <span className="font-bold text-blue-950 text-right">{car.engine_size || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center gap-3 py-1 border-b border-gray-50">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Fuel Type</span>
                <span className="font-bold text-blue-950 text-right">{car.fuel_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center gap-3 py-1 border-b border-gray-50">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Custom Paper</span>
                <span className="font-bold text-blue-950 text-right leading-tight">{car.custom_paper || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center gap-3 py-1">
                <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Color</span>
                <span className="font-bold text-blue-950 text-right">{car.color || 'N/A'}</span>
              </div>
            </div>

            {/* Key Features Pill Badges */}
            {car.keyfeatures && car.keyfeatures.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-blue-950 uppercase tracking-wider mb-2 font-sans">
                  Key Features
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {car.keyfeatures.map((feature, idx) => (
                    <span 
                      key={idx} 
                      className="text-[10px] font-semibold bg-blue-50 text-blue-900 border border-blue-100/50 px-2 py-0.5 rounded"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Book Inspection CTA button (Sleeker py-2.5 padding, closes modal on click) */}
          <div className="mt-8 pt-4 border-t border-gray-150">
            <Link
              to="/book-inspection"
              onClick={onClose}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm hover:scale-[1.01] transition duration-200 flex items-center justify-center space-x-2 font-sans tracking-wide uppercase text-[11px]"
            >
              <CalendarIcon className="h-4 w-4" />
              <span>Book Inspection</span>
            </Link>
          </div>
        </div>

      </Dialog.Panel>
    </Dialog>
  );
}
